import React from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Grid from '@material-ui/core/Grid';
import { Link as RouterLink } from 'react-router-dom';
import { alpha, useTheme } from '@material-ui/core/styles';
import LiveTvIcon from '@material-ui/icons/LiveTv';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import {
  BroadcastScreenVector,
  SignalWavesVector,
  NicheGridVector,
} from './GrillaHeroVectors';
import { getTnoHeroGradient } from '../../../../theme/tnoBrandColors';

const LOGO_LIGHT = '/images/logos/logo_white.PNG';

const DEFAULT_TITLE = 'TNO Network: El ecosistema de contenidos que amplifica tu marca';
const DEFAULT_SUBTITLE =
  'Desde 2014, pioneros en la convergencia de streaming, cultura podcast y periodismo digital. El TNO Digest en tnonetwork.com suma capa editorial y SEO al ecosistema.';
const DEFAULT_DESCRIPTION =
  'Conectamos marcas con audiencias activas que aprenden, disfrutan y evolucionan a través de múltiples formatos: live, On-Demand y autoridad editorial.';

const HERO_STATS = [
  { value: '30K+', label: 'conexiones mensuales al streaming' },
  { value: '90K+', label: 'seguidores en el ecosistema social' },
  { value: '25+', label: 'programas en la parrilla' },
  { value: '2014', label: 'año de fundación' },
];

const floatKeyframes = {
  '@keyframes heroFloatA': {
    '0%, 100%': { transform: 'translateY(0) rotate(0deg)' },
    '50%': { transform: 'translateY(-14px) rotate(1deg)' },
  },
  '@keyframes heroFloatB': {
    '0%, 100%': { transform: 'translateY(0)' },
    '50%': { transform: 'translateY(-10px)' },
  },
  '@keyframes heroPulse': {
    '0%, 100%': { opacity: 0.35, transform: 'scale(1)' },
    '50%': { opacity: 0.55, transform: 'scale(1.06)' },
  },
};

function parseHeroTitle(rawTitle) {
  const title = String(rawTitle || DEFAULT_TITLE).trim();
  const colon = title.indexOf(':');
  if (colon > 0) {
    return {
      brand: title.slice(0, colon).trim(),
      headline: title.slice(colon + 1).trim(),
    };
  }
  return { brand: 'TNO Network', headline: title };
}

function headlineToLines(headline) {
  const text = headline.replace(/\.$/, '').trim();
  const words = text.split(/\s+/);
  if (words.length <= 5) return [text];
  const mid = Math.ceil(words.length / 2);
  return [words.slice(0, mid).join(' '), words.slice(mid).join(' ')];
}

const Who = ({ themeMode = 'light', content = {} }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary?.main || '#00838f';
  const { brand, headline } = parseHeroTitle(content.title);
  const lines = headlineToLines(headline);
  const subtitle = content.subtitle || DEFAULT_SUBTITLE;
  const description = content.description || DEFAULT_DESCRIPTION;

  const heroGradient = getTnoHeroGradient(primary, secondary);

  return (
    <Box
      sx={{
        ...floatKeyframes,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: { xs: 3, md: 5 },
        width: '100%',
        maxWidth: '100%',
        mt: { xs: -2, sm: -4, md: -6 },
        mb: { xs: 3, md: 5 },
        background: heroGradient,
        color: '#fff',
        boxShadow: `0 32px 64px ${alpha(primary, 0.28)}`,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          width: { xs: 280, md: 420 },
          height: { xs: 280, md: 420 },
          borderRadius: '50%',
          top: -140,
          right: -100,
          background: 'radial-gradient(circle, rgba(255,255,255,0.14) 0%, transparent 68%)',
          animation: 'heroPulse 7s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: 200,
          height: 200,
          borderRadius: '50%',
          bottom: -70,
          left: -40,
          bgcolor: 'rgba(255,255,255,0.07)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `radial-gradient(circle at 20% 80%, ${alpha(secondary, 0.35)} 0%, transparent 45%)`,
          pointerEvents: 'none',
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          top: { md: 32 },
          right: { md: 40 },
          opacity: 0.32,
          animation: 'heroFloatA 5.5s ease-in-out infinite',
          display: { xs: 'none', md: 'block' },
          pointerEvents: 'none',
        }}
      >
        <BroadcastScreenVector width={180} />
      </Box>
      <Box
        sx={{
          position: 'absolute',
          bottom: { md: 24 },
          left: { md: 32 },
          opacity: 0.28,
          animation: 'heroFloatB 6.5s ease-in-out infinite',
          display: { xs: 'none', sm: 'block' },
          pointerEvents: 'none',
        }}
      >
        <SignalWavesVector width={110} />
      </Box>
      <Box
        sx={{
          position: 'absolute',
          top: '38%',
          right: { lg: '10%' },
          opacity: 0.22,
          animation: 'heroFloatB 7.2s ease-in-out infinite 0.6s',
          display: { xs: 'none', lg: 'block' },
          pointerEvents: 'none',
        }}
      >
        <NicheGridVector width={130} />
      </Box>

      <Box sx={{ position: 'relative', zIndex: 1, px: { xs: 2.5, sm: 4, md: 5 }, py: { xs: 4, md: 6 } }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} lg={7}>
            <Typography
              sx={{
                textTransform: 'uppercase',
                letterSpacing: 3,
                fontWeight: 700,
                fontSize: '0.78rem',
                opacity: 0.92,
                mb: 2,
              }}
            >
              Media Kit comercial · {brand}
            </Typography>

            <Box
              component="img"
              src={LOGO_LIGHT}
              alt={brand}
              sx={{
                height: { xs: 80, sm: 96, md: 112, lg: 128 },
                width: 'auto',
                maxWidth: { xs: '85%', sm: 420, md: 480 },
                display: 'block',
                mb: { xs: 3, md: 3.5 },
                filter: 'drop-shadow(0 10px 28px rgba(0,0,0,0.3))',
              }}
            />

            <Typography
              component="h1"
              sx={{
                fontWeight: 900,
                letterSpacing: '-0.04em',
                lineHeight: { xs: 1.02, md: 0.98 },
                fontSize: { xs: '2.35rem', sm: '3rem', md: '3.65rem', lg: '4rem' },
                mb: 2.5,
                maxWidth: 720,
              }}
            >
              {lines.map((line, i) => (
                <Box
                  component="span"
                  key={line}
                  sx={{
                    display: 'block',
                    ...(i === lines.length - 1 && {
                      background: 'linear-gradient(90deg, #fff 0%, rgba(255,255,255,0.88) 40%, #b2ebf2 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }),
                  }}
                >
                  {line}
                </Box>
              ))}
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: '1.02rem', md: '1.15rem' },
                lineHeight: 1.65,
                opacity: 0.92,
                maxWidth: 560,
                mb: 2,
              }}
            >
              {subtitle}
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: '0.92rem', md: '1rem' },
                lineHeight: 1.6,
                opacity: 0.78,
                maxWidth: 540,
                mb: 3,
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {description}
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mb: 3.5 }}>
              {HERO_STATS.map((stat) => (
                <Chip
                  key={stat.label}
                  label={
                    <Box component="span">
                      <Box component="span" sx={{ fontWeight: 900, mr: 0.6 }}>
                        {stat.value}
                      </Box>
                      {stat.label}
                    </Box>
                  }
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.16)',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.78rem',
                    height: 'auto',
                    py: 0.85,
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    '& .MuiChip-label': { whiteSpace: 'normal', lineHeight: 1.35 },
                  }}
                />
              ))}
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, maxWidth: '100%' }}>
              <Button
                component={RouterLink}
                to="/programacion"
                variant="contained"
                size="large"
                startIcon={<LiveTvIcon />}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  bgcolor: '#fff',
                  color: primary,
                  fontWeight: 800,
                  borderRadius: 999,
                  px: 3,
                  py: 1.35,
                  width: { xs: '100%', sm: 'auto' },
                  boxShadow: '0 12px 32px rgba(0,0,0,0.22)',
                  '&:hover': { bgcolor: '#f5f5f5' },
                }}
              >
                Ver programación
              </Button>
              <Button
                component={RouterLink}
                to="/paquetes"
                variant="outlined"
                size="large"
                startIcon={<LocalOfferIcon />}
                sx={{
                  borderRadius: 999,
                  borderColor: 'rgba(255,255,255,0.65)',
                  color: '#fff',
                  fontWeight: 700,
                  width: { xs: '100%', sm: 'auto' },
                  px: 2.75,
                  py: 1.25,
                  borderWidth: 2,
                  '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)', borderWidth: 2 },
                }}
              >
                Paquetes comerciales
              </Button>
              <Button
                component={RouterLink}
                to="/media-kits"
                variant="text"
                size="large"
                startIcon={<FolderOpenIcon />}
                sx={{
                  color: 'rgba(255,255,255,0.92)',
                  fontWeight: 600,
                  borderRadius: 999,
                  width: { xs: '100%', sm: 'auto' },
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                }}
              >
                Media Kits
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} lg={5} sx={{ display: { xs: 'none', lg: 'block' } }}>
            <Box
              sx={{
                borderRadius: 4,
                p: 3,
                bgcolor: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.22)',
                backdropFilter: 'blur(14px)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.15)',
              }}
            >
              <Typography
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 2,
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  opacity: 0.85,
                  mb: 2,
                }}
              >
                Ecosistema 360°
              </Typography>
              {[
                {
                  title: 'Live & Radio Visual (Streaming)',
                  text: 'Streaming global en señal en vivo, menciones y segmentos con contexto editorial.',
                },
                { title: 'On-Demand & Media Lab', text: '+40 producciones originales y narrativas de marca a medida.' },
                { title: 'TNO Digest & Web', text: 'Autoridad SEO, artículos y presencia permanente en tnonetwork.com.' },
              ].map((item, idx) => (
                <Box
                  key={item.title}
                  sx={{
                    mb: idx < 2 ? 2 : 0,
                    pl: 2,
                    borderLeft: `3px solid ${idx === 0 ? '#fff' : alpha('#fff', 0.45)}`,
                  }}
                >
                  <Typography sx={{ fontWeight: 800, fontSize: '1rem', mb: 0.5 }}>{item.title}</Typography>
                  <Typography sx={{ fontSize: '0.88rem', lineHeight: 1.5, opacity: 0.82 }}>{item.text}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

Who.propTypes = {
  themeMode: PropTypes.string,
  content: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    description: PropTypes.string,
  }),
};

Who.defaultProps = {
  themeMode: 'light',
  content: {},
};

export default Who;
